<template>
  <div class="tooltip__wrapper">
    <div
      ref="tooltip"
      role="tooltip"
      class="tooltip"
      aria-description="Tooltip"
      aria-expanded="false"
      tabindex="0"
    >
      <div>
        <slot name="label" />
      </div>
    </div>
    <div ref="contentEl" class="tooltip__content">
      <slot name="content" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import tippy from 'tippy.js'
import 'tippy.js/dist/tippy.css'
import type { TooltipPlacement } from '@/types'
import { removeAttributes } from '@/utils/helpers'

const tooltip = ref()
const contentEl = ref()
const tippyInstance = ref()

interface Props {
  placement: TooltipPlacement
}

const props = withDefaults(defineProps<Props>(), {
  placement: 'top',
})

onMounted(() => {
  const sanitizedContent = removeAttributes(contentEl.value)
  const content = sanitizedContent.innerHTML
  contentEl.value.remove()

  tippyInstance.value = tippy(tooltip.value, {
    content,
    allowHTML: true,
    theme: 'infermedica',
    placement: props.placement,
    interactive: true,
    appendTo: () => document.body,
    zIndex: 100,
  })
})

onUnmounted(() => {
  tippyInstance.value.destroy()
})
</script>

<style lang="scss">
.tooltip {
  position: absolute;
  bottom: 0;
  left: 0;
  word-break: keep-all;
  cursor: pointer;

  &__wrapper {
    position: relative;
    display: inline;
  }

  &__content {
    display: none;
  }
}

// tippy Infermedica theme
.tippy-box[data-theme~='infermedica'] {
  @include shadow-small-strong;

  padding: 12px;
  background-color: white;
  border-radius: 8px;
}

.tippy-box[data-theme~='infermedica'][data-placement^='top']
  > .tippy-arrow::before {
  border-top-color: white;
}

.tippy-box[data-theme~='infermedica'][data-placement^='bottom']
  > .tippy-arrow::before {
  border-bottom-color: white;
}

.tippy-box[data-theme~='infermedica'][data-placement^='left']
  > .tippy-arrow::before {
  border-left-color: white;
}

.tippy-box[data-theme~='infermedica'][data-placement^='right']
  > .tippy-arrow::before {
  border-right-color: white;
}
</style>
